import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { GuestRoute } from '@/components/auth/GuestRoute'
import { PermissionRoute } from '@/components/auth/PermissionRoute'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { SessionGuard } from '@/components/auth/SessionGuard'
import { AppShell } from '@/components/layout/AppShell'
import { CalendarPage } from '@/pages/CalendarPage'
import { LoginPage } from '@/pages/LoginPage'
import { OnlineBookingsPage } from '@/pages/OnlineBookingsPage'
import { PosPage } from '@/pages/PosPage'
import { SalesPage } from '@/pages/SalesPage'
import { RolesSetupPage } from '@/pages/admin/RolesSetupPage'
import { SendOffersPage } from '@/pages/SendOffersPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { TodayPage } from '@/pages/TodayPage'
import { BranchSetupPage } from '@/pages/admin/BranchSetupPage'
import { CompanySetupPage } from '@/pages/admin/CompanySetupPage'
import { UsersSetupPage } from '@/pages/admin/UsersSetupPage'
import { AuditLogPage } from '@/pages/admin/AuditLogPage'
import { CatalogMastersPage } from '@/pages/masters/CatalogMastersPage'
import { CustomersPage } from '@/pages/masters/CustomersPage'
import { CustomerCreatePage, CustomerEditPage } from '@/pages/masters/CustomerFormPage'
import { ServicesPage } from '@/pages/masters/ServicesPage'
import { StylistsPage } from '@/pages/masters/StylistsPage'
import { CashPage } from '@/pages/CashPage'
import { ProductsPage } from '@/pages/ProductsPage'
import { PackagesPage } from '@/pages/masters/PackagesPage'
import { CustomersRetentionPage } from '@/pages/CustomersRetentionPage'
import { InsightsPage } from '@/pages/InsightsPage'
import { LeadsPage } from '@/pages/LeadsPage'
import { HrPage } from '@/pages/HrPage'
import { ApprovalsPage } from '@/pages/ApprovalsPage'
import { AccountsDeskPage } from '@/pages/AccountsDeskPage'
import { PERMISSIONS } from '@/lib/permissions'
import { PublicBookLayout } from '@/components/booking/PublicBookLayout'
import { BookBranchCatalogPage } from '@/pages/book/BookBranchCatalogPage'
import { BookBranchHomePage } from '@/pages/book/BookBranchHomePage'
import { BookHubPage } from '@/pages/book/BookHubPage'
import { BookManagePage } from '@/pages/book/BookManagePage'

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || undefined

function App() {
  return (
    <BrowserRouter basename={routerBasename}>
      <Routes>
        <Route path="/book" element={<PublicBookLayout />}>
          <Route index element={<BookHubPage />} />
          <Route path="manage/:token" element={<BookManagePage />} />
          <Route path=":slug" element={<BookBranchHomePage />} />
          <Route path=":slug/catalog" element={<BookBranchCatalogPage />} />
        </Route>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <SessionGuard>
                <AppShell />
              </SessionGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<TodayPage />} />
          <Route
            path="calendar"
            element={
              <PermissionRoute permission={PERMISSIONS.APPOINTMENTS_MANAGE}>
                <CalendarPage />
              </PermissionRoute>
            }
          />
          <Route
            path="online-bookings"
            element={
              <PermissionRoute permission={PERMISSIONS.APPOINTMENTS_MANAGE}>
                <OnlineBookingsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="pos"
            element={
              <PermissionRoute permission={PERMISSIONS.POS_ACCESS}>
                <PosPage />
              </PermissionRoute>
            }
          />
          <Route
            path="cash"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_CASH}|${PERMISSIONS.ACCOUNTS_ACCESS}`}>
                <CashPage />
              </PermissionRoute>
            }
          />
          <Route
            path="products"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_PRODUCTS}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <ProductsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="packages"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_PACKAGES}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <PackagesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="customers/retention"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_CUSTOMERS}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <CustomersRetentionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="customers/new"
            element={
              <PermissionRoute permission={`${PERMISSIONS.CUSTOMERS_CREATE}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <CustomerCreatePage />
              </PermissionRoute>
            }
          />
          <Route
            path="customers/:customerId/edit"
            element={
              <PermissionRoute permission={`${PERMISSIONS.CUSTOMERS_EDIT}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <CustomerEditPage />
              </PermissionRoute>
            }
          />
          <Route
            path="customers"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_CUSTOMERS}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <CustomersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="services"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_SERVICES}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <ServicesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="staff"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_STAFF}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <StylistsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="sales"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <SalesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="reports"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <InsightsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="leads"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_LEADS}|${PERMISSIONS.CUSTOMERS_VIEW}`}>
                <LeadsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="hr"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_HR}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <HrPage />
              </PermissionRoute>
            }
          />
          <Route
            path="approvals"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_APPROVALS}|${PERMISSIONS.MASTERS_MANAGE}`}>
                <ApprovalsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="accounts"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_ACCOUNTS}|${PERMISSIONS.ACCOUNTS_ACCESS}`}>
                <AccountsDeskPage />
              </PermissionRoute>
            }
          />
          <Route
            path="send-offers"
            element={
              <PermissionRoute permission={`${PERMISSIONS.MENU_SEND_OFFERS}|${PERMISSIONS.ADMIN_FULL}`}>
                <SendOffersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="settings"
            element={
              <PermissionRoute
                permission={`${PERMISSIONS.MENU_SETTINGS}|${PERMISSIONS.ADMIN_FULL}|${PERMISSIONS.ADMIN_USERS}`}
              >
                <SettingsPage />
              </PermissionRoute>
            }
          />

          {/* Owner-only setup (linked from Settings, not in sidebar) */}
          <Route
            path="admin/company"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <CompanySetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/branches"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <BranchSetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/roles"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_USERS}>
                <RolesSetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_USERS}>
                <UsersSetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/audit-logs"
            element={
              <PermissionRoute permission={`${PERMISSIONS.ADMIN_FULL}|${PERMISSIONS.ACCOUNTS_ACCESS}`}>
                <AuditLogPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/catalog"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <CatalogMastersPage />
              </PermissionRoute>
            }
          />

          {/* Legacy Salon ERP URLs → Salon Desk */}
          <Route path="stylists" element={<Navigate to="/staff" replace />} />
          <Route path="masters/stylists" element={<Navigate to="/staff" replace />} />
          <Route path="masters/services" element={<Navigate to="/services" replace />} />
          <Route path="masters/customers" element={<Navigate to="/customers" replace />} />
          <Route path="insights" element={<Navigate to="/reports" replace />} />
          <Route path="hq" element={<Navigate to="/" replace />} />
          <Route path="inventory" element={<Navigate to="/products" replace />} />
          <Route path="stylist" element={<Navigate to="/" replace />} />
          <Route path="workspace/commissions" element={<Navigate to="/" replace />} />
          <Route path="masters/service-categories" element={<Navigate to="/services" replace />} />
          <Route path="masters/packages" element={<Navigate to="/packages" replace />} />
          <Route path="masters/products" element={<Navigate to="/products" replace />} />
          <Route path="masters/chairs" element={<Navigate to="/settings" replace />} />
          <Route path="masters/pos-quick-keys" element={<Navigate to="/settings" replace />} />
          <Route path="masters/import" element={<Navigate to="/settings" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
